import { debounce, isArray } from 'lodash';
import { getEditorPointerId } from '@wix/editorx-core-api';
import { getFontPresetToWixThemeVariableMap } from '@wix/wix-site-theme-variables';

import type {
  AppData,
  BuilderFontStyleParam,
  ColorStyleParam,
  CompRef,
  FontStyleParam,
  TpaSetStyleParamCallback,
  TpaStyleParamValue,
} from '@wix/document-services-types';
import type { CompPointer } from '@wix/editor-document-logic-api';
import type { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import type { TPAMessage, TPAMessageCallback } from '../types/tpaMessage';
import {
  convertLegacyColorKeyToWixGlobalVariable,
  convertWixGlobalVariableToLegacyColorKey,
  extractWixGlobalVariable,
} from '../utils/globalVariablesUtils';
import { stringAsCssVar } from '../../../../utils';

const PARAM_FONT_PREFIX = 'param_font_';

type ColorStyleParamWithValue = ColorStyleParam & {
  param: {
    value: {
      color: {
        value: string;
        name: string;
        reference?: string;
      };
    };
  };
};

const isLegacyColorStyleParam = (
  value: TpaStyleParamValue,
): value is ColorStyleParamWithValue => {
  return !!(
    value.type === 'color' &&
    value.param.value.color &&
    value.param.value.color.name &&
    typeof value.param.value.color.name === 'string' &&
    value.param.value.color.name.startsWith('color_')
  );
};

const replaceLegacyColorKey = (
  change: TpaStyleParamValue,
): TpaStyleParamValue => {
  if (!isLegacyColorStyleParam(change)) {
    return change;
  }
  const replacement = convertLegacyColorKeyToWixGlobalVariable(
    change.param.value.color.name,
  );
  if (!replacement) {
    return change;
  }
  return {
    ...change,
    param: {
      ...change.param,
      value: {
        ...change.param.value,
        color: {
          ...change.param.value.color,
          name: stringAsCssVar(replacement),
        },
      },
    },
  };
};

const legacyFontPresets = getFontPresetToWixThemeVariableMap();

const isLegacyFontStyleParam = (
  value: TpaStyleParamValue,
): value is FontStyleParam => {
  return !!(
    value.type === 'font' &&
    'fontStyleParam' in value.param.value &&
    value.param.value.fontStyleParam &&
    value.param.value.preset &&
    legacyFontPresets[value.param.value.preset]
  );
};

const replaceLegacyFontKey = (
  change: TpaStyleParamValue,
): TpaStyleParamValue => {
  if (!isLegacyFontStyleParam(change)) {
    return change;
  }
  if (!change.param.value.preset) {
    return change;
  }
  const cssVarName = legacyFontPresets[change.param.value.preset];
  if (!cssVarName) {
    return change;
  }

  const newChange: BuilderFontStyleParam = {
    type: change.type,
    key: change.key,
    param: {
      ...change.param,
      value: {
        builderFontStyle: true,
        cssVarName,
        name: '',
        value: stringAsCssVar(cssVarName),
        textDecoration: change.param.value.style.underline
          ? 'underline'
          : 'none',
      },
    },
  };
  return newChange;
};

const createCallbackWithMappingToLegacyColors = (
  callback: TpaSetStyleParamCallback,
  change: TpaStyleParamValue,
): TpaSetStyleParamCallback => {
  return (arg) => {
    const changeKey = 'param_color_' + change.key;
    const changedValue = arg.styleProperties[changeKey];
    if (typeof changedValue !== 'string') {
      callback(arg);
      return;
    }
    const variableName = extractWixGlobalVariable(changedValue);
    if (!variableName) {
      callback(arg);
      return;
    }
    const legacyKey = convertWixGlobalVariableToLegacyColorKey(variableName);
    if (!legacyKey) {
      callback(arg);
      return;
    }
    callback({
      styleProperties: {
        ...arg.styleProperties,
        [changeKey]: legacyKey,
      },
    });
  };
};

const createCallbackWithMappingToLegacyFonts = (
  callback: TpaSetStyleParamCallback,
  change: FontStyleParam,
): TpaSetStyleParamCallback => {
  return (arg) => {
    const changeKey = PARAM_FONT_PREFIX + change.key;
    const rawValue = arg.styleProperties[changeKey];
    if (typeof rawValue !== 'string') {
      callback(arg);
      return;
    }
    try {
      const value = JSON.parse(rawValue);
      if (typeof value !== 'object' || !('builderFontStyle' in value)) {
        callback(arg);
        return;
      }
      const legacyValueString = JSON.stringify(change.param.value);

      const newArg = {
        ...arg,
        styleProperties: {
          ...arg.styleProperties,
          [changeKey]: legacyValueString,
        },
      };
      callback(newArg);
    } catch (cause) {
      console.error(new Error('Failed to parse callback value', { cause }));
      callback(arg);
    }
  };
};

type CompRefToStyleChangesMap = {
  [key: string]: {
    compRef: CompRef;
    callbacks: TpaSetStyleParamCallback[];
    changes: TpaStyleParamValue[];
  };
};

export const createTpaStyleService = (
  platformApiMethods: PlatformApiMethods,
): TpaStyleService => {
  const changesQueue: any[] = [];

  const debouncedUpdateStyle = debounce((isGlobal: boolean) => {
    const tempQueue: any[] = [];

    while (changesQueue.length > 0) {
      tempQueue.push(changesQueue.pop());
    }

    tempQueue.reverse();

    const compRefToStyleChangesMap: CompRefToStyleChangesMap = tempQueue.reduce(
      (
        _compRefToStyleChangesMap,
        { styleToUpdate, callback, compRef, pointerId },
      ) => {
        const changes = _compRefToStyleChangesMap[pointerId]?.changes || [];
        const callbacks = _compRefToStyleChangesMap[pointerId]?.callbacks || [];
        const messageDataAsArray = isArray(styleToUpdate)
          ? styleToUpdate
          : [styleToUpdate];
        const newCallbackAsArray = callback ? [callback] : [];

        return {
          ..._compRefToStyleChangesMap,
          [pointerId]: {
            compRef,
            changes: [...changes, ...messageDataAsArray],
            callbacks: [...callbacks, ...newCallbackAsArray],
          },
        };
      },
      {},
    );

    const getStyleParamsOptions = (item: CompRefToStyleChangesMap[string]) => {
      if (platformApiMethods.editor.selectColor) {
        const styleParams = item.changes.map((change) => {
          return replaceLegacyFontKey(replaceLegacyColorKey(change));
        });
        const callbacks = item.callbacks.map((callback, index) => {
          const change = item.changes[index];
          return isLegacyColorStyleParam(change)
            ? createCallbackWithMappingToLegacyColors(callback, change)
            : isLegacyFontStyleParam(change)
            ? createCallbackWithMappingToLegacyFonts(callback, change)
            : callback;
        });

        return {
          isGlobal,
          compRef: item.compRef,
          styleParams,
          callbacks,
        };
      }

      return {
        isGlobal,
        // - TODO - refactor TpaStyleParamValue in DM
        compRef: item.compRef,
        styleParams: item.changes,
        callbacks: item.callbacks,
      };
    };

    Object.keys(compRefToStyleChangesMap).forEach((key) => {
      const item = compRefToStyleChangesMap[key];
      const options = getStyleParamsOptions(item);
      const appData = {} as AppData;

      platformApiMethods.document.tpa.setStyleParams?.(appData, '', options);

      return platformApiMethods;
    });
  }, 50);

  const setStyleParam = async (
    compRef: CompRef,
    styleToUpdate: TPAMessage['data'],
    callback?: TPAMessageCallback,
    isGlobal?: boolean,
  ) => {
    const pointerId = getEditorPointerId(compRef as CompPointer);

    changesQueue.push({ styleToUpdate, callback, compRef, pointerId });
    debouncedUpdateStyle(!!isGlobal);
  };

  return { setStyleParam };
};

export interface TpaStyleService {
  setStyleParam: (
    compRef: CompRef,
    styleToUpdate: TPAMessage['data'],
    callback: TPAMessageCallback,
    isGlobal?: boolean,
  ) => Promise<void>;
}
